import * as React from 'react';
import {
  Star,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import type { ProjectDetails } from '@/types';
import { PROJECT_STATUSES } from '@/lib/statuses';

type EnrichedProject = ProjectDetails & {
  isFavourite?: boolean;
  lastAccessedDate?: Date;
};

interface ProjectSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: EnrichedProject[];
  value?: string;
  onSelect: (projectId: string) => void;
  onToggleFavourite: (projectId: string) => void;
  isFavourite: (projectId: string) => boolean;
  selectedProjectId?: string;
  configuredStatuses?: string[];
}

type SortColumn = 'name' | 'billingCode' | 'clientName' | 'industry' | 'projectManager' | 'status' | 'startDate' | 'endDate';
type SortDirection = 'asc' | 'desc' | null;

const SEARCH_DEBOUNCE_MS = 300;
const ITEMS_PER_PAGE = 50;

export function ProjectSearchModal({
  open,
  onOpenChange,
  projects,
  value,
  onSelect,
  onToggleFavourite,
  isFavourite,
  selectedProjectId,
  configuredStatuses,
}: ProjectSearchModalProps) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [sortColumn, setSortColumn] = React.useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc');
  const [showFilters, setShowFilters] = React.useState(false);
  const [displayedItems, setDisplayedItems] = React.useState(ITEMS_PER_PAGE);
  
  // Filter states (pending until "Apply Filters" is clicked)
  const [pendingShowFavouritesOnly, setPendingShowFavouritesOnly] = React.useState(false);
  const [pendingStatusFilter, setPendingStatusFilter] = React.useState<string[]>([]);
  const [pendingProjectManagerFilter, setPendingProjectManagerFilter] = React.useState<string[]>([]);
  const [pendingClientFilter, setPendingClientFilter] = React.useState<string[]>([]);
  
  // Applied filters (actually used for filtering)
  const [showFavouritesOnly, setShowFavouritesOnly] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<string[]>([]);
  const [projectManagerFilter, setProjectManagerFilter] = React.useState<string[]>([]);
  const [clientFilter, setClientFilter] = React.useState<string[]>([]);

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Determine which statuses to offer in the filter dropdown
  const availableStatusOptions = React.useMemo(() => {
    if (!configuredStatuses || configuredStatuses.length === 0) {
      return PROJECT_STATUSES;
    }
    return PROJECT_STATUSES.filter(s => configuredStatuses.includes(s.value));
  }, [configuredStatuses]);

  // Pre-fill search with selected project's billing code when modal opens with a selection.
  // Using billingCode instead of name ensures a unique match when multiple projects share the same name.
  React.useEffect(() => {
    if (open && selectedProjectId) {
      const selectedProject = projects.find(p => String(p.id) === String(selectedProjectId));
      if (selectedProject) {
        setSearchQuery(selectedProject.billingCode || selectedProject.name);
      }
    }
  }, [open, selectedProjectId, projects]);

  // Reset on close
  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setDebouncedSearch('');
      setSortColumn('name');
      setSortDirection('asc');
      setShowFilters(false);
      setDisplayedItems(ITEMS_PER_PAGE);
      setShowFavouritesOnly(false);
      setStatusFilter([]);
      setProjectManagerFilter([]);
      setClientFilter([]);
      setPendingShowFavouritesOnly(false);
      setPendingStatusFilter([]);
      setPendingProjectManagerFilter([]);
      setPendingClientFilter([]);
    }
  }, [open]);

  // Fuzzy search function
  const fuzzyMatch = React.useCallback((text: string, query: string): boolean => {
    if (!text) return false;
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase();
    if (textLower.includes(queryLower)) return true;
    let queryIndex = 0;
    for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
      if (textLower[i] === queryLower[queryIndex]) {
        queryIndex++;
      }
    }
    return queryIndex === queryLower.length;
  }, []);

  // Get unique values for filters
  const uniqueProjectManagers = React.useMemo(() => {
    const managers = new Set<string>();
    projects.forEach(p => {
      if (p.projectManager) managers.add(p.projectManager);
    });
    return Array.from(managers).sort();
  }, [projects]);

  const uniqueClients = React.useMemo(() => {
    const clients = new Set<string>();
    projects.forEach(p => {
      if (p.clientName) clients.add(p.clientName);
    });
    return Array.from(clients).sort();
  }, [projects]);

  // Filter and search projects
  const filteredProjects = React.useMemo(() => {
    let filtered = [...projects];

    // Apply favourites filter
    if (showFavouritesOnly) {
      filtered = filtered.filter(p => isFavourite(String(p.id)));
    }

    // Apply status filter: when no explicit user filter is set, default to configured statuses
    if (statusFilter.length > 0) {
      filtered = filtered.filter(p => statusFilter.includes(p.status));
    } else if (configuredStatuses && configuredStatuses.length > 0) {
      filtered = filtered.filter(p => configuredStatuses.includes(p.status));
    }

    // Apply project manager filter
    if (projectManagerFilter.length > 0) {
      filtered = filtered.filter(p => p.projectManager && projectManagerFilter.includes(p.projectManager));
    }

    // Apply client filter
    if (clientFilter.length > 0) {
      filtered = filtered.filter(p => p.clientName && clientFilter.includes(p.clientName));
    }

    // Apply search
    if (debouncedSearch) {
      // When the query exactly matches a billing code, show only that project
      // (avoids e.g. "9531E" also matching "9531E.01" via substring)
      const exactCodeMatches = filtered.filter(
        p => p.billingCode.toLowerCase() === debouncedSearch.toLowerCase()
      );
      if (exactCodeMatches.length > 0) {
        filtered = exactCodeMatches;
      } else {
        filtered = filtered.filter(project => {
          return (
            fuzzyMatch(project.name, debouncedSearch) ||
            fuzzyMatch(project.billingCode, debouncedSearch) ||
            fuzzyMatch(project.clientName || '', debouncedSearch) ||
            fuzzyMatch(project.projectManager || '', debouncedSearch) ||
            fuzzyMatch(project.status, debouncedSearch)
          );
        });
      }
    }

    return filtered;
  }, [projects, debouncedSearch, showFavouritesOnly, statusFilter, projectManagerFilter, clientFilter, fuzzyMatch, isFavourite]);

  // Sort projects
  const sortedProjects = React.useMemo(() => {
    if (!sortDirection) return filteredProjects;

    return [...filteredProjects].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'billingCode':
          aValue = a.billingCode.toLowerCase();
          bValue = b.billingCode.toLowerCase();
          break;
        case 'clientName':
          aValue = (a.clientName || '').toLowerCase();
          bValue = (b.clientName || '').toLowerCase();
          break;
        case 'industry':
          aValue = ''; // Empty for now
          bValue = '';
          break;
        case 'projectManager':
          aValue = (a.projectManager || '').toLowerCase();
          bValue = (b.projectManager || '').toLowerCase();
          break;
        case 'status':
          aValue = a.status.toLowerCase();
          bValue = b.status.toLowerCase();
          break;
        case 'startDate':
        case 'endDate':
          aValue = ''; // Empty for now
          bValue = '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredProjects, sortColumn, sortDirection]);

  // Paginated projects for infinite scroll
  const displayedProjects = React.useMemo(() => {
    return sortedProjects.slice(0, displayedItems);
  }, [sortedProjects, displayedItems]);

  // Handle column header click for sorting
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> null (no sort)
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn('name'); // Reset to default
      } else {
        setSortDirection('asc');
      }
    } else {
      // New column, start with ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Handle project selection
  const handleSelect = (projectId: string) => {
    // If selecting the same project, just close the modal
    if (String(projectId) === String(selectedProjectId)) {
      onOpenChange(false);
      return;
    }

    onSelect(projectId);
    onOpenChange(false);
  };

  // Handle star click
  const handleStarClick = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    e.preventDefault();
    onToggleFavourite(projectId);
  };

  // Apply filters
  const handleApplyFilters = () => {
    setShowFavouritesOnly(pendingShowFavouritesOnly);
    setStatusFilter(pendingStatusFilter);
    setProjectManagerFilter(pendingProjectManagerFilter);
    setClientFilter(pendingClientFilter);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setPendingShowFavouritesOnly(false);
    setPendingStatusFilter([]);
    setPendingProjectManagerFilter([]);
    setPendingClientFilter([]);
    setShowFavouritesOnly(false);
    setStatusFilter([]);
    setProjectManagerFilter([]);
    setClientFilter([]);
  };

  // Load more items for infinite scroll
  const handleLoadMore = () => {
    setDisplayedItems(prev => prev + ITEMS_PER_PAGE);
  };

  // Render sort icon
  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column || !sortDirection) {
      return null;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-1 h-4 w-4" />
    );
  };

  // Get status badge variant
  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'in progress':
        return 'default';
      case 'planning':
        return 'secondary';
      case 'on hold':
      case 'cancelled':
        return 'destructive';
      case 'completed':
        return 'outline';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <DialogTitle className="text-base">Select Project</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Main content */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {/* Search bar */}
            <div className="px-4 py-3 border-b shrink-0">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowFilters(!showFilters)}
                  className={showFilters ? "bg-muted" : ""}
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                {sortedProjects.length} project{sortedProjects.length !== 1 ? 's' : ''} found
              </div>
            </div>

            {/* Data table with scroll */}
            <div className="flex-1 overflow-auto min-h-0">
              <div className="min-w-max px-4">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="w-[50px] bg-background">
                        <Star className="h-4 w-4 text-muted-foreground" />
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[200px]"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center">
                          Project Name
                          {renderSortIcon('name')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[150px]"
                        onClick={() => handleSort('billingCode')}
                      >
                        <div className="flex items-center">
                          Project Code
                          {renderSortIcon('billingCode')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[180px]"
                        onClick={() => handleSort('clientName')}
                      >
                        <div className="flex items-center">
                          Client Name
                          {renderSortIcon('clientName')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[150px]"
                        onClick={() => handleSort('industry')}
                      >
                        <div className="flex items-center">
                          Industry
                          {renderSortIcon('industry')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[180px]"
                        onClick={() => handleSort('projectManager')}
                      >
                        <div className="flex items-center">
                          Project Manager
                          {renderSortIcon('projectManager')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[120px]"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center">
                          Status
                          {renderSortIcon('status')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[120px]"
                        onClick={() => handleSort('startDate')}
                      >
                        <div className="flex items-center">
                          Start Date
                          {renderSortIcon('startDate')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[120px]"
                        onClick={() => handleSort('endDate')}
                      >
                        <div className="flex items-center">
                          End Date
                          {renderSortIcon('endDate')}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                <TableBody>
                  {displayedProjects.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No projects match these results. Please try another filter/search term.
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {/* Project list */}
                      {displayedProjects.map((project) => (
                        <TableRow
                          key={project.id}
                          className={cn(
                            'cursor-pointer hover:bg-muted/50',
                            String(project.id) === String(selectedProjectId) && 'bg-primary/10'
                          )}
                          onClick={() => handleSelect(String(project.id))}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Star
                              className={cn(
                                'h-4 w-4 cursor-pointer transition-colors',
                                isFavourite(String(project.id))
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-muted-foreground hover:text-yellow-400'
                              )}
                              onClick={(e) => handleStarClick(e, String(project.id))}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{project.name}</TableCell>
                          <TableCell>{project.billingCode}</TableCell>
                          <TableCell>{project.clientName || '-'}</TableCell>
                          <TableCell className="text-muted-foreground">-</TableCell>
                          <TableCell>{project.projectManager || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={getStatusVariant(project.status)}>
                              {PROJECT_STATUSES.find(s => s.value === project.status)?.label || project.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">-</TableCell>
                          <TableCell className="text-muted-foreground">-</TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                </TableBody>
                </Table>

                {/* Load more button for infinite scroll */}
                {displayedProjects.length < sortedProjects.length && (
                  <div className="flex justify-center py-4">
                    <Button variant="outline" onClick={handleLoadMore}>
                      Load More ({sortedProjects.length - displayedProjects.length} remaining)
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Advanced filters sidebar */}
          {showFilters && (
            <>
              <Separator orientation="vertical" />
              <div className="w-[300px] border-l flex flex-col min-h-0">
                <div className="px-4 py-2.5 border-b flex items-center justify-between shrink-0">
                  <h3 className="font-semibold text-sm">Filters</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setShowFilters(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3">
                  <div className="space-y-4">
                    {/* Show Favourites Only */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Favourites</Label>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="favourites"
                          checked={pendingShowFavouritesOnly}
                          onCheckedChange={(checked) => setPendingShowFavouritesOnly(checked === true)}
                        />
                        <label
                          htmlFor="favourites"
                          className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Show only favourites
                        </label>
                      </div>
                    </div>

                    {/* Status Filter */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Status</Label>
                      <MultiSelect
                        options={availableStatusOptions.map(s => ({ label: s.label, value: s.value }))}
                        value={pendingStatusFilter}
                        onValueChange={setPendingStatusFilter}
                        placeholder="Select statuses..."
                      />
                    </div>

                    {/* Project Manager Filter */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Project Manager</Label>
                      <MultiSelect
                        options={uniqueProjectManagers.map(pm => ({ label: pm, value: pm }))}
                        value={pendingProjectManagerFilter}
                        onValueChange={setPendingProjectManagerFilter}
                        placeholder="Select managers..."
                      />
                    </div>

                    {/* Client Filter */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Client</Label>
                      <MultiSelect
                        options={uniqueClients.map(c => ({ label: c, value: c }))}
                        value={pendingClientFilter}
                        onValueChange={setPendingClientFilter}
                        placeholder="Select clients..."
                      />
                    </div>

                    {/* Industry Filter - Placeholder */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-muted-foreground">Industry</Label>
                      <p className="text-xs text-muted-foreground">Coming soon</p>
                    </div>

                    {/* Date Range Filter - Placeholder */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-muted-foreground">Date Range</Label>
                      <p className="text-xs text-muted-foreground">Coming soon</p>
                    </div>
                  </div>
                </div>

                <div className="px-4 py-2.5 border-t space-y-2 shrink-0">
                  <Button className="w-full" size="sm" onClick={handleApplyFilters}>
                    Apply Filters
                  </Button>
                  <Button variant="outline" className="w-full" size="sm" onClick={handleClearFilters}>
                    Clear All Filters
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
